import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Every section owns a pastel wash and a matching ink. Written out as complete
// class strings — Tailwind only ever sees literals, never a template-built class.
const CATEGORY_TINT = {
    news: "bg-tint-news text-ink-news",
    "personal-finance": "bg-tint-personal-finance text-ink-personal-finance",
    "debt-management": "bg-tint-debt-management text-ink-debt-management",
    "small-business": "bg-tint-small-business text-ink-small-business",
    investing: "bg-tint-investing text-ink-investing",
    markets: "bg-tint-markets text-ink-markets",
};
const TINT_FALLBACK = "bg-accent-soft text-accent";

// Unknown or missing categories fall back to the soft accent wash.
function tintFor(category) {
    return CATEGORY_TINT[category] || TINT_FALLBACK;
}

// The kicker is a filled pastel capsule, not a coloured word.
function Chip({ category, children, className = "" }) {
    return (
        <span className={`eyebrow inline-flex items-center rounded-full px-3 py-1.5 ${tintFor(category)} ${className}`}>
            {children}
        </span>
    );
}

function Byline({ article, className = "" }) {
    return (
        <div className={`flex items-center gap-2 font-mono text-[11px] text-dim ${className}`}>
            {article.author && <span className="truncate max-w-[170px]">{article.author}</span>}
            {article.author && <span className="w-1 h-1 rounded-full bg-line-strong" />}
            <span>{timeAgo(article.published_at)}</span>
        </div>
    );
}

export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    // Hero: one big pillow. The picture is inset inside the card with a margin
    // of white all round, and the copy sits underneath rather than over a scrim.
    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up rounded-2xl bg-surface border border-line shadow-card p-3 sm:p-4 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
                <div className="aspect-[16/10] sm:aspect-video overflow-hidden rounded-xl bg-surface-raised">
                    {article.thumbnail && (
                        <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                    )}
                </div>
                <div className="px-2 sm:px-4 pt-6 pb-3">
                    <Chip category={article.category}>{label}</Chip>
                    <h2 className="font-display text-2xl sm:text-4xl lg:text-[2.6rem] font-semibold leading-[1.12] tracking-[-0.015em] mt-4 text-foreground group-hover:text-accent transition-colors duration-200">
                        {article.title}
                    </h2>
                    {article.excerpt && (
                        <p className="mt-4 text-[15px] text-dim max-w-2xl leading-relaxed line-clamp-2">
                            {article.excerpt}
                        </p>
                    )}
                    <Byline article={article} className="mt-5" />
                </div>
            </Link>
        );
    }

    // Compact: a soft row that fills with the accent wash on hover.
    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-4 p-3 rounded-xl hover:bg-surface-raised transition-colors duration-200"
            >
                <div className="flex-1 min-w-0">
                    <Chip category={article.category} className="!text-[10px] !px-2.5 !py-1">{label}</Chip>
                    <h3 className="font-display text-[15px] font-semibold leading-snug mt-2.5 text-foreground group-hover:text-accent transition-colors duration-200 line-clamp-3">
                        {article.title}
                    </h3>
                    <span className="font-mono text-[11px] text-dim mt-2 block">{timeAgo(article.published_at)}</span>
                </div>
                {article.thumbnail && (
                    <div className="w-[92px] h-[76px] shrink-0 overflow-hidden rounded-lg bg-surface-raised">
                        <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                        />
                    </div>
                )}
            </Link>
        );
    }

    // Default: 24px pillow, 20px picture floating inside it.
    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group flex flex-col animate-fade-up rounded-xl bg-surface border border-line shadow-card p-3 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-lift"
        >
            <div className="aspect-[3/2] overflow-hidden rounded-lg bg-surface-raised">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                    />
                ) : (
                    <div className="w-full h-full bg-surface-raised" />
                )}
            </div>
            <div className="flex flex-col flex-1 px-2.5 pt-5 pb-2">
                <Chip category={article.category} className="self-start !text-[10px] !px-2.5 !py-1">{label}</Chip>
                <h3 className="font-display text-lg font-semibold leading-snug mt-3 text-foreground group-hover:text-accent transition-colors duration-200">
                    {article.title}
                </h3>
                {article.excerpt && (
                    <p className="mt-2.5 text-sm text-dim leading-relaxed line-clamp-3">{article.excerpt}</p>
                )}
                <Byline article={article} className="mt-auto pt-5 border-t border-line" />
            </div>
        </Link>
    );
}

export { timeAgo, tintFor };
